import { useEffect, useState } from 'react';
import { useSEO } from '@/hooks/useSEO';
import { supabase } from '@/lib/supabase';

interface ReferralClient {
  id: number;
  customer_name: string;
  customer_email: string | null;
  customer_whatsapp: string | null;
  referral_code: string | null;
  email_sent: boolean;
  email_sent_at: string | null;
  reviewed: boolean;
  created_at: string;
}

interface HelpRequest {
  id: number;
  person_name: string;
  person_phone: string | null;
  person_address: string | null;
  person_pix: string;
  pix_owner_name: string | null;
  referrer_name: string | null;
  referrer_phone: string | null;
  status: string;
  created_at: string;
}

const PASSWORD_KEY = 'hop_admin_password';

function useClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return { copied, copy };
}

export default function PainelClientesPage() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const [clients, setClients] = useState<ReferralClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerOk, setRegisterOk] = useState(false);

  const [sendingId, setSendingId] = useState<number | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendOk, setSendOk] = useState<number | null>(null);

  const [helpRequests, setHelpRequests] = useState<HelpRequest[]>([]);
  const [loadingHelp, setLoadingHelp] = useState(false);
  const [helpError, setHelpError] = useState<string | null>(null);
  const [helpActionId, setHelpActionId] = useState<number | null>(null);

  useSEO({
    title: 'Painel de Clientes | Home Office Premium',
    description: 'Acesso restrito.',
    keywords: 'painel clientes, home office premium',
    path: '/painel-clientes',
  });

  useEffect(() => {
    const saved = sessionStorage.getItem(PASSWORD_KEY);
    if (saved) {
      setPassword(saved);
      setAuthed(true);
    }
    setCheckingAuth(false);
  }, []);

  const loadClients = async (pwd: string) => {
    setLoadingClients(true);
    setListError(null);
    try {
      const { data, error } = await supabase.functions.invoke('client-admin', {
        body: { action: 'list', password: pwd },
      });
      if (error) throw error;
      const parsed = data as { clients?: ReferralClient[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setClients(parsed?.clients ?? []);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Erro ao carregar clientes.');
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    if (authed && password) {
      loadClients(password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const loadHelpRequests = async (pwd: string) => {
    setLoadingHelp(true);
    setHelpError(null);
    try {
      const { data, error } = await supabase.functions.invoke('help-requests', {
        body: { action: 'list', password: pwd },
      });
      if (error) throw error;
      const parsed = data as { requests?: HelpRequest[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setHelpRequests(parsed?.requests ?? []);
    } catch (e) {
      setHelpError(e instanceof Error ? e.message : 'Erro ao carregar solicitações de ajuda.');
    } finally {
      setLoadingHelp(false);
    }
  };

  useEffect(() => {
    if (authed && password) {
      loadHelpRequests(password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoggingIn(true);
    setLoginError(null);
    try {
      const { data, error } = await supabase.functions.invoke('client-admin', {
        body: { action: 'verify', password: password.trim() },
      });
      if (error) throw error;
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      sessionStorage.setItem(PASSWORD_KEY, password.trim());
      setAuthed(true);
      loadClients(password.trim());
    } catch {
      setLoginError('Senha incorreta. Tente novamente.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PASSWORD_KEY);
    setPassword('');
    setAuthed(false);
    setClients([]);
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) {
      setRegisterError('Digite o nome do cliente.');
      return;
    }
    if (!email.trim() && !whatsapp.trim()) {
      setRegisterError('Informe ao menos um e-mail ou WhatsApp.');
      return;
    }
    setRegistering(true);
    setRegisterError(null);
    setRegisterOk(false);
    try {
      const { data, error } = await supabase.functions.invoke('client-admin', {
        body: {
          action: 'register',
          password,
          customer_name: name.trim(),
          customer_email: email.trim(),
          customer_whatsapp: whatsapp.trim(),
        },
      });
      if (error) throw error;
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setName('');
      setEmail('');
      setWhatsapp('');
      setRegisterOk(true);
      setTimeout(() => setRegisterOk(false), 3000);
      loadClients(password);
    } catch (err) {
      setRegisterError(err instanceof Error ? err.message : 'Erro ao cadastrar.');
    } finally {
      setRegistering(false);
    }
  };

  const handleSend = async (client: ReferralClient) => {
    setSendingId(client.id);
    setSendError(null);
    setSendOk(null);
    try {
      const { data, error } = await supabase.functions.invoke('client-admin', {
        body: { action: 'send_review', password, client_id: client.id },
      });
      if (error) throw error;
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setSendOk(client.id);
      setTimeout(() => setSendOk(null), 3000);
      loadClients(password);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Erro ao enviar e-mail.');
    } finally {
      setSendingId(null);
    }
  };

  const handleMarkHelped = async (req: HelpRequest) => {
    setHelpActionId(req.id);
    setHelpError(null);
    try {
      const { data, error } = await supabase.functions.invoke('help-requests', {
        body: { action: 'update_status', password, request_id: req.id, status: 'ajudado' },
      });
      if (error) throw error;
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      loadHelpRequests(password);
    } catch (err) {
      setHelpError(err instanceof Error ? err.message : 'Erro ao atualizar status.');
    } finally {
      setHelpActionId(null);
    }
  };

  const clipboard = useClipboard();

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Tela de login
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F6F6F4] flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm bg-white rounded-2xl border border-hop-border p-8"
        >
          <div className="w-14 h-14 flex items-center justify-center bg-[#E8610A] text-white rounded-xl mx-auto mb-4">
            <i className="ri-lock-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold text-gray-900 text-center mb-1">
            Acesso restrito
          </h1>
          <p className="text-sm text-gray-500 text-center mb-6">
            Painel de clientes da Home Office Premium
          </p>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
            Senha
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Digite sua senha"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
          />
          {loginError && (
            <p className="text-sm text-red-600 mt-3">{loginError}</p>
          )}
          <button
            type="submit"
            disabled={loggingIn}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-3 rounded-xl hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 mt-4"
          >
            {loggingIn ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Entrando...
              </>
            ) : (
              <>
                <i className="ri-login-box-line"></i>
                Entrar
              </>
            )}
          </button>
        </form>
      </div>
    );
  }

  // Painel
  return (
    <div className="min-h-screen bg-[#F6F6F4]">
      <header className="bg-white border-b border-hop-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#E8610A] text-white rounded-lg">
              <i className="ri-user-star-line text-lg"></i>
            </div>
            <div>
              <h1 className="font-bold text-gray-900 text-sm">Painel de Clientes</h1>
              <p className="text-xs text-gray-500">Home Office Premium</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-r-line"></i>
            Sair
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Formulário de cadastro */}
        <section className="bg-white rounded-2xl border border-hop-border p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
              <i className="ri-user-add-line text-lg"></i>
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Cadastrar cliente que já comprou</h2>
              <p className="text-sm text-gray-500">
                Depois de cadastrar, você pode enviar o e-mail de avaliação em um clique.
              </p>
            </div>
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Maria Silva"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  E-mail
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="cliente@email.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                WhatsApp
              </label>
              <input
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="(81) 99999-9999"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
              />
            </div>

            {registerError && (
              <p className="text-sm text-red-600">{registerError}</p>
            )}
            {registerOk && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                <i className="ri-checkbox-circle-line"></i>
                Cliente cadastrado com sucesso!
              </div>
            )}

            <button
              type="submit"
              disabled={registering}
              className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {registering ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Cadastrando...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Cadastrar cliente
                </>
              )}
            </button>
          </form>
        </section>

        {/* Lista de clientes */}
        <section className="bg-white rounded-2xl border border-hop-border p-6">
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
                <i className="ri-contacts-line text-lg"></i>
              </div>
              <div>
                <h2 className="font-bold text-gray-900">Clientes cadastrados</h2>
                <p className="text-sm text-gray-500">
                  {clients.length} cliente{clients.length === 1 ? '' : 's'} no total
                </p>
              </div>
            </div>
            <button
              onClick={() => loadClients(password)}
              disabled={loadingClients}
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              <i className={`ri-refresh-line ${loadingClients ? 'animate-spin' : ''}`}></i>
              Atualizar
            </button>
          </div>

          {listError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
              {listError}
            </div>
          )}
          {sendError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
              {sendError}
            </div>
          )}

          {loadingClients && clients.length === 0 ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                <i className="ri-user-line text-xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">
                Nenhum cliente cadastrado ainda. Cadastre o primeiro acima.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-hop-border">
                    <th className="py-3 pr-4 font-semibold">Cliente</th>
                    <th className="py-3 pr-4 font-semibold">Contato</th>
                    <th className="py-3 pr-4 font-semibold">Código</th>
                    <th className="py-3 pr-4 font-semibold">Status</th>
                    <th className="py-3 font-semibold text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id} className="border-b border-hop-border/60">
                      <td className="py-3 pr-4">
                        <span className="font-semibold text-gray-900">
                          {client.customer_name}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-gray-600 text-xs leading-relaxed">
                          {client.customer_email && (
                            <div>{client.customer_email}</div>
                          )}
                          {client.customer_whatsapp && (
                            <div>{client.customer_whatsapp}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        {client.referral_code ? (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold text-gray-900">
                              {client.referral_code}
                            </span>
                            <button
                              onClick={() => clipboard.copy(client.referral_code ?? '')}
                              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-[#E8610A] cursor-pointer"
                              title="Copiar código"
                            >
                              <i
                                className={
                                  clipboard.copied
                                    ? 'ri-check-line text-green-600'
                                    : 'ri-file-copy-line text-sm'
                                }
                              ></i>
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {client.email_sent ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                            <i className="ri-check-line"></i>
                            E-mail enviado
                          </span>
                        ) : client.customer_email ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                            <i className="ri-time-line"></i>
                            Não enviado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                            <i className="ri-forbid-line"></i>
                            Sem e-mail
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {client.customer_email ? (
                          sendOk === client.id ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 whitespace-nowrap">
                              <i className="ri-check-double-line"></i>
                              Enviado!
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSend(client)}
                              disabled={sendingId === client.id}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8610A] border border-[#E8610A] px-3 py-1.5 rounded-lg hover:bg-[#E8610A]/5 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
                            >
                              {sendingId === client.id ? (
                                <>
                                  <i className="ri-loader-4-line animate-spin"></i>
                                  Enviando...
                                </>
                              ) : (
                                <>
                                  <i className="ri-mail-send-line"></i>
                                  Enviar e-mail
                                </>
                              )}
                            </button>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Caixinha de Ajuda */}
        <section className="bg-white rounded-2xl border border-hop-border p-6">
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
                <i className="ri-hand-heart-line text-lg"></i>
              </div>
              <div>
                <h2 className="font-bold text-gray-900">Caixinha de Ajuda 💛</h2>
                <p className="text-sm text-gray-500">
                  {helpRequests.length} solicitação{helpRequests.length === 1 ? '' : 'ões'} de ajuda
                </p>
              </div>
            </div>
            <button
              onClick={() => loadHelpRequests(password)}
              disabled={loadingHelp}
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              <i className={`ri-refresh-line ${loadingHelp ? 'animate-spin' : ''}`}></i>
              Atualizar
            </button>
          </div>

          {helpError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
              {helpError}
            </div>
          )}

          {loadingHelp && helpRequests.length === 0 ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : helpRequests.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                <i className="ri-heart-line text-xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-500">
                Nenhuma solicitação de ajuda ainda. Quando alguém indicar uma pessoa,
                ela vai aparecer aqui.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {helpRequests.map((req) => (
                <div
                  key={req.id}
                  className={`border rounded-xl p-5 transition-colors ${
                    req.status === 'ajudado'
                      ? 'bg-green-50/50 border-green-200'
                      : 'bg-hop-bg-alt border-hop-border'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                          req.status === 'ajudado'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-[#E8610A]/10 text-[#E8610A]'
                        }`}
                      >
                        <i
                          className={
                            req.status === 'ajudado'
                              ? 'ri-check-double-line text-lg'
                              : 'ri-heart-3-line text-lg'
                          }
                        ></i>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">
                            {req.person_name}
                          </h3>
                          {req.status === 'ajudado' ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 border border-green-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                              <i className="ri-check-line"></i>
                              Ajuda enviada
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                              <i className="ri-time-line"></i>
                              Pendente
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                          {req.person_phone && (
                            <div className="flex items-center gap-1.5">
                              <i className="ri-phone-line"></i>
                              {req.person_phone}
                            </div>
                          )}
                          {req.person_address && (
                            <div className="flex items-start gap-1.5">
                              <i className="ri-map-pin-line mt-0.5"></i>
                              {req.person_address}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <i className="ri-qr-code-line"></i>
                            <span className="font-medium">Pix:</span> {req.person_pix}
                            {req.pix_owner_name && (
                              <span className="text-gray-400">
                                ({req.pix_owner_name})
                              </span>
                            )}
                          </div>
                          {req.referrer_name && (
                            <div className="flex items-center gap-1.5">
                              <i className="ri-user-shared-line"></i>
                              Indicado por: {req.referrer_name}
                              {req.referrer_phone && ` — ${req.referrer_phone}`}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-gray-400">
                            <i className="ri-calendar-line"></i>
                            {new Date(req.created_at).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {req.status !== 'ajudado' && (
                      <button
                        onClick={() => handleMarkHelped(req)}
                        disabled={helpActionId === req.id}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8610A] border border-[#E8610A] px-3 py-1.5 rounded-lg hover:bg-[#E8610A]/5 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
                      >
                        {helpActionId === req.id ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Marcando...
                          </>
                        ) : (
                          <>
                            <i className="ri-check-double-line"></i>
                            Marcar como ajudado
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}